'use client';

import { ActionIcon, Group, Stack, Text, Title } from '@mantine/core';
import ChevronLeft from '@material-design-icons/svg/filled/chevron_left.svg';
import React, { useEffect, useState } from 'react';

import { VoterVerificationForm } from '@/components';

interface BoostSwayModalProps {
  isShowing: boolean;
  leaderName: string;
  topicName: string;
  isVerified: boolean;
  personId: string;
  refetchIssue: () => void;
  onClose?: () => void;
}

export function BoostSwayModal({
  isShowing,
  leaderName,
  topicName,
  isVerified,
  personId,
  refetchIssue,
  onClose,
}: BoostSwayModalProps) {
  const [shouldShow, setShouldShow] = useState(isShowing);
  const [verificationSuccessful, setVerificationSuccessful] = useState(false);

  // Reset state when isShowing prop changes
  useEffect(() => {
    setShouldShow(isShowing);
    setVerificationSuccessful(isVerified);
  }, [isShowing, isVerified]);

  // Set up timer to close modal after successful verification
  useEffect(() => {
    if (verificationSuccessful && isShowing) {
      const timer = setTimeout(() => {
        setShouldShow(false);
        if (onClose) {
          onClose();
        }
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [verificationSuccessful, onClose, isShowing]);

  const handleVerificationSuccess = () => {
    setVerificationSuccessful(true);
    refetchIssue();
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Stack gap="lg">
      <Group gap="xs">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => {
            setShouldShow(false);
            if (onClose) onClose();
          }}
          style={{
            top: 0,
            left: 0,
          }}
        >
          <ChevronLeft />
        </ActionIcon>
        <Text size="sm">Back</Text>
      </Group>
      <Title order={2}>
        You are supporting <b>{leaderName}</b> on <b>{topicName}</b>!{' '}
        {!isVerified &&
          !verificationSuccessful &&
          'Boost your impact by verifying your voter registration.'}
        {verificationSuccessful && // TODO this will eventually be replaced with an animation
          'You are a verified voter. This message will close in a few seconds.'}
      </Title>
      {!isVerified && !verificationSuccessful && (
        <VoterVerificationForm
          hideTitle={true}
          personId={personId}
          onVerificationSuccess={handleVerificationSuccess}
        />
      )}
    </Stack>
  );
}
